import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { mockData } from '../mock';
import FieldHeader from './FieldHeader';
import CinematicHero from './CinematicHero';
import StudyWithMe from './StudyWithMe';
import CareerJourney from './CareerJourney';
import FieldCommunicationWheel from './FieldCommunicationWheel';
import BookCollection from './BookCollection';
import CreativeProjects from './CreativeProjects';
import RelationalLexicon from './RelationalLexicon';
import WhitePapers from './WhitePapers';
import EventsAppearances from './EventsAppearances';
import MediaEmergence from './MediaEmergence';
import SubstackLibrary from './SubstackLibrary';
import SupportWork from './SupportWork';
import PressKit from './PressKit';
import FieldConnection from './FieldConnection';
import FieldFooter from './FieldFooter';
import WaterCanvas from './WaterCanvas';
import AlvinsPlace from './AlvinsPlace';
import WorkWithBetsy from './WorkWithBetsy';
import AlvinSerial from './AlvinSerial';
import ExitIntentModal from './ExitIntentModal';

const Portfolio = () => {
  const [isFieldActive, setIsFieldActive] = useState(false);
  const [waterParticles, setWaterParticles] = useState([]);
  const canvasRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsFieldActive(true);
    }, 1200);

    const particles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      size: Math.random() * 4 + 1,
      opacity: Math.random() * 0.3 + 0.1,
      life: Math.random() * 100
    }));
    setWaterParticles(particles);

    return () => clearTimeout(timer);
  }, []);

  // Reliably scroll to the hash (e.g., /#work-with-betsy) BEFORE the first
  // paint, so direct-link visitors land at the section instantly with no flash
  // through Hero / Study With Me / Events / etc., even as images load and the
  // page height grows. Hardened against iOS Safari quirks (QR scans, phantom
  // touches, lazy paint, native anchor-scroll racing React).
  useLayoutEffect(() => {
    // Disable the browser's automatic scroll-to-top on hash changes.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const initialHash = window.location.hash.replace('#', '');

    // CRITICAL for iOS Safari: scroll to top FIRST to override Safari's
    // attempt to scroll-to-anchor on its own (which fires before React
    // has mounted the section's content, landing the user in a blank area).
    if (initialHash) {
      window.scrollTo(0, 0);
    }

    if (!initialHash) return;

    let userInteracted = false;
    let touchStartY = null;

    // Only consider the user "interacting" if they actually drag with intent
    // (> 30px). iOS often fires phantom touchstart from QR scans / link opens.
    const onTouchStart = (e) => {
      touchStartY = e.touches?.[0]?.clientY ?? null;
    };
    const onTouchMove = (e) => {
      if (touchStartY === null) return;
      const dy = Math.abs((e.touches?.[0]?.clientY ?? touchStartY) - touchStartY);
      if (dy > 30) userInteracted = true;
    };
    const onWheel = () => { userInteracted = true; };
    const onKeyDown = (e) => {
      // Only meaningful keyboard scroll keys
      if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(e.key)) {
        userInteracted = true;
      }
    };

    const scrollToHash = (behavior = 'instant') => {
      if (userInteracted) return false;
      const el = document.getElementById(initialHash);
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      // Only re-scroll if section is not already near the top of viewport (within 8px)
      if (Math.abs(rect.top) <= 8 && rect.height > 0) return true;
      el.scrollIntoView({ behavior, block: 'start' });
      return true;
    };

    // 1. Synchronous attempt before first paint
    scrollToHash('instant');

    // 2. Every animation frame for first ~1s (handles initial layout passes)
    const rafs = [];
    let cancelled = false;
    let count = 0;
    const tick = () => {
      if (cancelled || count > 60) return;
      scrollToHash('instant');
      count += 1;
      rafs.push(requestAnimationFrame(tick));
    };
    rafs.push(requestAnimationFrame(tick));

    // 3. Re-scroll whenever the page height grows (images shift layout)
    let lastHeight = document.body.scrollHeight;
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        const h = document.body.scrollHeight;
        if (h !== lastHeight) {
          lastHeight = h;
          scrollToHash('instant');
        }
      });
      resizeObserver.observe(document.body);
    }

    // 4. Per-image-load re-scroll
    const imageHandlers = [];
    const watchImages = () => {
      const imgs = document.querySelectorAll('img');
      imgs.forEach((img) => {
        if (!img.complete) {
          const handler = () => scrollToHash('instant');
          img.addEventListener('load', handler, { once: true });
          img.addEventListener('error', handler, { once: true });
          imageHandlers.push({ img, handler });
        }
      });
    };
    watchImages();
    // Watch for newly-added images too (for lazy-rendered components)
    setTimeout(watchImages, 500);
    setTimeout(watchImages, 1500);
    setTimeout(watchImages, 3000);

    // 5. Periodic safety net for 8s (mobile Safari sometimes needs this)
    const periodicTimers = [
      setTimeout(() => scrollToHash('instant'), 500),
      setTimeout(() => scrollToHash('instant'), 1500),
      setTimeout(() => scrollToHash('instant'), 3000),
      setTimeout(() => scrollToHash('instant'), 5000),
      setTimeout(() => scrollToHash('instant'), 7500),
    ];

    // 6. Listen for real interaction (not phantom touches)
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('keydown', onKeyDown);

    // 7. Stop everything after 10 seconds (page should be fully settled)
    const stopTimer = setTimeout(() => {
      cancelled = true;
      if (resizeObserver) resizeObserver.disconnect();
    }, 10000);

    // React to in-page hashchange (anchor clicks within the SPA)
    const onHashChange = () => {
      const h = window.location.hash.replace('#', '');
      if (!h) return;
      const el = document.getElementById(h);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener('hashchange', onHashChange);

    return () => {
      cancelled = true;
      rafs.forEach((id) => cancelAnimationFrame(id));
      if (resizeObserver) resizeObserver.disconnect();
      imageHandlers.forEach(({ img, handler }) => {
        img.removeEventListener('load', handler);
        img.removeEventListener('error', handler);
      });
      periodicTimers.forEach(clearTimeout);
      clearTimeout(stopTimer);
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-blue-900 text-slate-100 overflow-x-hidden relative">
      {/* Enhanced Water Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 opacity-25">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 via-slate-800/25 to-blue-800/35 animate-water-flow-1"></div>
          <div className="absolute inset-0 bg-gradient-to-tl from-slate-700/20 via-blue-700/15 to-slate-800/25 animate-water-flow-2"></div>
        </div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-water-ripple-1"></div>
          <div className="absolute bottom-1/3 right-1/4 w-72 h-72 bg-slate-400/20 rounded-full blur-3xl animate-water-ripple-2"></div>
          <div className="absolute top-2/3 left-1/2 w-80 h-80 bg-blue-600/15 rounded-full blur-3xl animate-water-ripple-3"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-slate-800/40 to-blue-900/50"></div>
      </div>
      <WaterCanvas particles={waterParticles} />

      {/* Main Content */}
      <div className="relative z-10">
        <FieldHeader />
        <CinematicHero data={mockData.hero} images={mockData.fieldImages} />
        <StudyWithMe />
        <WorkWithBetsy />
        <EventsAppearances />
        <FieldCommunicationWheel />
        <BookCollection data={mockData.books} />
        <AlvinSerial />
        <MediaEmergence />
        <SubstackLibrary />
        <RelationalLexicon />
        <WhitePapers />
        <CreativeProjects data={mockData.projects} />
        <CareerJourney data={mockData.journey} />
        <SupportWork />
        <PressKit />
        <AlvinsPlace />
        <FieldConnection data={mockData.connection} />
        <FieldFooter />
      </div>

      {/* Exit-intent newsletter capture (first-time visitors only) */}
      <ExitIntentModal />
    </div>
  );
};

export default Portfolio;
